import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { FaTimes, FaCheck, FaCrown, FaRocket, FaStar, FaGlobeAmericas } from 'react-icons/fa';
import { getUserLimits } from '../utils/tokenUtils';

const UpgradeModal = ({ isOpen, onClose, user, onUpgrade, highlightSection = null }) => {
  // Premium kullanıcılar için enterprise'ı otomatik seç
  const getInitialPlan = () => {
    const limits = getUserLimits();
    const userPlan = limits?.plan || 'free';
    if (userPlan === 'premium') {
      return 'enterprise';
    }
    return 'premium';
  };
  
  const [selectedPlan, setSelectedPlan] = useState(getInitialPlan());
  const [isProcessing, setIsProcessing] = useState(false);
  const [isYearly, setIsYearly] = useState(false);
  const [planLimits, setPlanLimits] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal açıldığında selectedPlan'ı güncelle
  useEffect(() => {
    if (isOpen) {
      setSelectedPlan(getInitialPlan());
    }
  }, [isOpen]);

  // Plan limitlerini çek
  useEffect(() => {
    const fetchPlanLimits = async () => {
      if (!isOpen) return;
      
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/auth/plan-limits', {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setPlanLimits(data.planLimits || []);
        }
      } catch (error) {
        console.error('Error fetching plan limits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlanLimits();
  }, [isOpen]);


  // Helper function to highlight features based on section
  const getHighlightedFeatures = (features, section) => {
    if (!section || planLimits.length === 0) {
      return features.map(feature => ({
        text: feature,
        highlighted: false
      }));
    }
    
    // Veritabanından gelen plan limitlerinden section mapping oluştur
    const sectionMap = {};
    planLimits.forEach(planLimit => {
      const limits = planLimit.limits;
      const planName = planLimit.plan;
      
      // Her plan için section'a göre feature string oluştur
      if (!sectionMap[section]) sectionMap[section] = [];
      
      switch (section) {
        case 'projects':
          if (limits.maxProjects === -1) {
            sectionMap[section].push('Unlimited Projects');
          } else {
            sectionMap[section].push(`${limits.maxProjects} Project${limits.maxProjects > 1 ? 's' : ''}`);
          }
          break;
        case 'documents':
          if (limits.maxDocuments === -1) {
            sectionMap[section].push('Unlimited Documents');
          } else {
            sectionMap[section].push(`${limits.maxDocuments} Document${limits.maxDocuments > 1 ? 's' : ''}`);
          }
          break;
        case 'shortlinks':
          if (limits.maxShortLinks === -1) {
            sectionMap[section].push('Unlimited Short Links');
          } else {
            sectionMap[section].push(`${limits.maxShortLinks} Short Link${limits.maxShortLinks > 1 ? 's' : ''}`);
          }
          break;
        case 'support':
          if (limits.maxSupportModules === -1) {
            sectionMap[section].push('Unlimited Support Modules');
          } else {
            sectionMap[section].push(`${limits.maxSupportModules} Support Module${limits.maxSupportModules > 1 ? 's' : ''}`);
          }
          break;
        case 'landingpages':
          if (limits.maxLandingPages === -1) {
            sectionMap[section].push('Unlimited Landing Pages');
          } else {
            sectionMap[section].push(`${limits.maxLandingPages} Landing Page${limits.maxLandingPages > 1 ? 's' : ''}`);
          }
          break;
        case 'internalads':
          if (limits.maxInternalAds === -1) {
            sectionMap[section].push('Unlimited Internal Ads');
          } else {
            sectionMap[section].push(`${limits.maxInternalAds} Internal Ad${limits.maxInternalAds > 1 ? 's' : ''}`);
          }
          break;
      }
    });
    
    const highlightItems = sectionMap[section] || [];
    
    return features.map(feature => ({
      text: feature,
      highlighted: highlightItems.some(item => feature.includes(item.split(' ')[0]))
    }));
  };

  // Helper function to highlight current plan's feature if user is at limit
  const getCurrentPlanHighlight = (features, section, userPlan) => {
    if (!section || !userPlan || planLimits.length === 0) {
      return features.map(feature => ({
        text: feature,
        highlighted: false
      }));
    }
    
    // Veritabanından gelen plan limitlerinden current plan feature'ını bul
    const currentPlanLimit = planLimits.find(planLimit => planLimit.plan === userPlan);
    if (!currentPlanLimit) {
      return features.map(feature => ({
        text: feature,
        highlighted: false
      }));
    }
    
    const limits = currentPlanLimit.limits;
    let currentPlanFeature = '';
    
    switch (section) {
      case 'projects':
        if (limits.maxProjects === -1) {
          currentPlanFeature = 'Unlimited Projects';
        } else {
          currentPlanFeature = `${limits.maxProjects} Project${limits.maxProjects > 1 ? 's' : ''}`;
        }
        break;
      case 'documents':
        if (limits.maxDocuments === -1) {
          currentPlanFeature = 'Unlimited Documents';
        } else {
          currentPlanFeature = `${limits.maxDocuments} Document${limits.maxDocuments > 1 ? 's' : ''}`;
        }
        break;
      case 'shortlinks':
        if (limits.maxShortLinks === -1) {
          currentPlanFeature = 'Unlimited Short Links';
        } else {
          currentPlanFeature = `${limits.maxShortLinks} Short Link${limits.maxShortLinks > 1 ? 's' : ''}`;
        }
        break;
      case 'support':
        if (limits.maxSupportModules === -1) {
          currentPlanFeature = 'Unlimited Support Modules';
        } else {
          currentPlanFeature = `${limits.maxSupportModules} Support Module${limits.maxSupportModules > 1 ? 's' : ''}`;
        }
        break;
      case 'landingpages':
        if (limits.maxLandingPages === -1) {
          currentPlanFeature = 'Unlimited Landing Pages';
        } else {
          currentPlanFeature = `${limits.maxLandingPages} Landing Page${limits.maxLandingPages > 1 ? 's' : ''}`;
        }
        break;
      case 'internalads':
        if (limits.maxInternalAds === -1) {
          currentPlanFeature = 'Unlimited Internal Ads';
        } else {
          currentPlanFeature = `${limits.maxInternalAds} Internal Ad${limits.maxInternalAds > 1 ? 's' : ''}`;
        }
        break;
    }
    
    return features.map(feature => ({
      text: feature,
      highlighted: feature === currentPlanFeature
    }));
  };

  // Plan limitlerinden dinamik planlar oluştur
  const getPlans = () => {
    if (planLimits.length === 0) {
      // Veritabanından veri gelmezse boş array döndür
      return [];
    }

    // Free planını filtrele, sadece premium ve enterprise göster
    const filteredPlans = planLimits.filter(plan => plan.plan !== 'free');

    return filteredPlans.map(planLimit => {
      const limits = planLimit.limits;
      const pricing = planLimit.pricing;
      const features = planLimit.features;
      
      // Limitleri feature string'lerine çevir
      const featureStrings = [];
      
      // Projects
      if (limits.maxProjects === -1) {
        featureStrings.push('Unlimited Projects');
      } else {
        featureStrings.push(`${limits.maxProjects} Project${limits.maxProjects > 1 ? 's' : ''}`);
      }
      
      // Documents
      if (limits.maxDocuments === -1) {
        featureStrings.push('Unlimited Documents');
      } else {
        featureStrings.push(`${limits.maxDocuments} Document${limits.maxDocuments > 1 ? 's' : ''}`);
      }
      
      // Short Links
      if (limits.maxShortLinks === -1) {
        featureStrings.push('Unlimited Short Links');
      } else {
        featureStrings.push(`${limits.maxShortLinks} Short Link${limits.maxShortLinks > 1 ? 's' : ''}`);
      }
      
      // Support Modules
      if (limits.maxSupportModules === -1) {
        featureStrings.push('Unlimited Support Modules');
      } else {
        featureStrings.push(`${limits.maxSupportModules} Support Module${limits.maxSupportModules > 1 ? 's' : ''}`);
      }
      
      // Landing Pages
      if (limits.maxLandingPages === -1) {
        featureStrings.push('Unlimited Landing Pages');
      } else {
        featureStrings.push(`${limits.maxLandingPages} Landing Page${limits.maxLandingPages > 1 ? 's' : ''}`);
      }
      
      // Internal Ads
      if (limits.maxInternalAds === -1) {
        featureStrings.push('Unlimited Internal Ads');
      } else {
        featureStrings.push(`${limits.maxInternalAds} Internal Ad${limits.maxInternalAds > 1 ? 's' : ''}`);
      }
      
      
      // Features
      if (features.prioritySupport) {
        featureStrings.push('Priority Support');
      } else {
        featureStrings.push('Basic Support');
      }
      
      // Icon ve renk seçimi
      let icon, color, bgColor, borderColor, popular = false;
      
      switch (planLimit.plan) {
        case 'premium':
          icon = FaCrown;
          color = 'text-gray-300';
          bgColor = 'bg-gray-800';
          borderColor = 'border-gray-500';
          popular = true;
          break;
        case 'enterprise':
          icon = FaRocket;
          color = 'text-gray-300';
          bgColor = 'bg-gray-800';
          borderColor = 'border-gray-600';
          break;
        default:
          icon = FaStar;
          color = 'text-gray-400';
          bgColor = 'bg-gray-800';
          borderColor = 'border-gray-600';
      }
      
      return {
        id: planLimit.plan,
        name: planLimit.plan.charAt(0).toUpperCase() + planLimit.plan.slice(1),
        price: `$${pricing.monthly}`,
        monthlyPrice: `$${pricing.monthly}`,
        yearlyPrice: `$${pricing.yearly}`,
        period: 'month',
        description: planLimit.description || 'Advanced features',
        features: featureStrings,
        icon,
        color,
        bgColor,
        borderColor,
        popular
      };
    });
  };

  const plans = getPlans();

  const handleUpgrade = async () => {
    
    // Validate selectedPlan
    if (!selectedPlan) {
      alert('Please select a plan');
      return;
    }
    
    setIsProcessing(true);
    try {
      // Get plan price
      const plan = plans.find(p => p.id === selectedPlan);
      if (!plan) {
        throw new Error('Selected plan not found');
      }
      
      const period = isYearly ? 'yearly' : 'monthly';
      
      // Get Stripe price ID with validation
      const priceId = getStripePriceId(selectedPlan, isYearly);
      
      // Validate user data
      if (!user) {
        throw new Error('User not found - please refresh the page and try again');
      }
      
      const userId = user.id || user._id;
      if (!userId) {
        throw new Error('User ID not found - please refresh the page and try again');
      }
      
      if (!user.email) {
        throw new Error('User email not found - please refresh the page and try again');
      }
      
      // Create Stripe checkout session
      const response = await fetch('/api/payment/stripe/subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          userId: userId,
          userEmail: user.email,
          successUrl: `${window.location.origin}/dashboard?subscription=success&source=upgrade&plan=${selectedPlan}&billing=${period}`,
          cancelUrl: `${window.location.origin}/dashboard?subscription=cancelled&source=upgrade`
        })
      });

      const result = await response.json();

      if (result.success && result.url) {
        // Redirect to Stripe checkout
        window.location.href = result.url;
      } else {
        throw new Error(result.message || 'Failed to initialize payment');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Payment initialization failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to get Stripe price ID
  const getStripePriceId = (planId, isYearly) => {
    // These should match your actual Stripe price IDs
    const priceMap = {
      'premium': {
        monthly: 'price_1SKgYVQVh1A8agwU9LhQHf9n', // Stripe Price ID (monthly)
        yearly: 'price_1SKgZDQVh1A8agwUcZYZSivG'    // Stripe Price ID (yearly)
      },
      'enterprise': {
        monthly: 'price_1SKgafQVh1A8agwUlN3sD8PT', // Stripe Price ID (monthly)
        yearly: 'price_1SKgc2QVh1A8agwUGItU3bPz'    // Stripe Price ID (yearly)
      }
    };
    
    // Check if planId exists and is valid
    if (!planId || !priceMap[planId]) {
      console.error('Invalid planId:', planId);
      throw new Error('Invalid plan selected');
    }
    
    const period = isYearly ? 'yearly' : 'monthly';
    const priceId = priceMap[planId][period];
    
    if (!priceId) {
      console.error('Price ID not found for plan:', planId, 'period:', period);
      throw new Error('Price ID not found for selected plan');
    }
    
    return priceId;
  };

  const limits = getUserLimits();
  const currentUserPlan = limits?.plan || 'free';
  const currentPlan = plans.find(plan => plan.id === currentUserPlan);
  
  const getPlanPrice = (plan) => {
    return isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  };
  
  const getPlanPeriod = (plan) => {
    return isYearly ? 'year' : plan.period;
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-5xl sm:max-w-4xl md:max-w-5xl transform overflow-hidden rounded-2xl bg-gray-900 p-4 sm:p-6 md:p-8 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4 sm:mb-6 md:mb-8">
                  <DialogTitle as="h3" className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                    Choose Your Plan
                  </DialogTitle>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <FaTimes className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="flex justify-center items-center py-6 sm:py-8 md:py-12">
                    <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-b-2 border-blue-500"></div>
                    <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-400">Loading plans...</span>
                  </div>
                )}

                {/* Billing Toggle */}
                <div className="flex justify-center mb-4 sm:mb-6 md:mb-8">
                  <div className="bg-gray-800 rounded-lg p-1 flex">
                    <button
                      onClick={() => setIsYearly(false)}
                      className={`px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                        !isYearly 
                          ? 'bg-white text-gray-900' 
                          : 'text-gray-300 hover:text-white'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setIsYearly(true)}
                      className={`px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                        isYearly 
                          ? 'bg-white text-gray-900' 
                          : 'text-gray-300 hover:text-white'
                      }`}
                    >
                      Yearly
                      <span className="ml-1 text-xs bg-green-600 text-white px-1.5 sm:px-2 py-0.5 rounded-full">
                        Save 17%
                      </span>
                    </button>
                  </div>
                </div>

                {!loading && (
                  <div className="grid grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10 mb-4 sm:mb-6 md:mb-8">
                    {plans.map((plan) => {
                    const IconComponent = plan.icon;
                    const isSelected = selectedPlan === plan.id;
                    const isCurrentPlan = plan.id === currentUserPlan;
                    
                    return (
                      <div
                        key={plan.id}
                        className={`relative rounded-lg sm:rounded-xl border-2 p-2 sm:p-3 md:p-4 lg:p-6 cursor-pointer transition-all duration-200 ${
                          isSelected 
                            ? `${plan.borderColor} ${plan.bgColor} shadow-lg scale-105` 
                            : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                        } ${isCurrentPlan ? 'opacity-75' : ''}`}
                        onClick={() => !isCurrentPlan && setSelectedPlan(plan.id)}
                      >
                        {plan.popular && !isCurrentPlan && (
                          <div className="absolute -top-1 sm:-top-2 md:-top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-blue-600 text-white px-1.5 sm:px-2 md:px-3 py-0.5 rounded-full text-xs font-semibold">
                              <span className="hidden sm:inline">Most Popular</span>
                              <span className="sm:hidden">Popular</span>
                            </span>
                          </div>
                        )}
                        
                        {isCurrentPlan && (
                          <div className="absolute -top-1 sm:-top-2 md:-top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-gray-700 text-white px-1.5 sm:px-2 md:px-3 py-0.5 rounded-full text-xs font-semibold">
                              <span className="hidden sm:inline">Current Plan</span>
                              <span className="sm:hidden">Current</span>
                            </span>
                          </div>
                        )}

                        <div className="text-center">
                          <div className={`inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full mb-1 sm:mb-2 md:mb-3 lg:mb-4 ${
                            isSelected ? 'bg-white bg-opacity-20' : 'bg-gray-700'
                          }`}>
                            <IconComponent className={`h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 ${isSelected ? 'text-white' : plan.color}`} />
                          </div>
                          
                          <h3 className={`text-xs sm:text-sm md:text-base lg:text-lg font-bold mb-1 ${isSelected ? 'text-white' : 'text-white'}`}>
                            {plan.name}
                          </h3>
                          
                          <div className="mb-1 sm:mb-2 md:mb-3 lg:mb-4">
                            <div className="flex items-center justify-center space-x-1">
                              {isYearly && (plan.id === 'premium' || plan.id === 'enterprise') && (
                                <span className={`text-xs sm:text-sm md:text-base lg:text-lg line-through ${isSelected ? 'text-white text-opacity-50' : 'text-gray-500'}`}>
                                  ${plan.id === 'premium' ? '120' : '1200'}
                                </span>
                              )}
                              <span className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold ${isSelected ? 'text-white' : 'text-white'}`}>
                                {getPlanPrice(plan)}
                              </span>
                            </div>
                            <div className="text-center">
                              <span className={`text-xs ${isSelected ? 'text-white text-opacity-80' : 'text-gray-400'}`}>
                                /{getPlanPeriod(plan)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <ul className="space-y-0.5 sm:space-y-1 md:space-y-1.5 lg:space-y-2">
                          {(() => {
                            // Only highlight current plan's feature if user is at limit
                            const isCurrentPlan = plan.id === user?.subscription?.plan;
                            const featuresToShow = isCurrentPlan && highlightSection 
                              ? getCurrentPlanHighlight(plan.features, highlightSection, user?.subscription?.plan)
                              : plan.features.map(feature => ({
                                  text: feature,
                                  highlighted: false
                                }));
                            
                            return featuresToShow.map((feature, index) => {
                              // 0 olan limitlerde X iconu, diğerlerinde check iconu
                              const isZeroLimit = feature.text.startsWith('0 ') && !feature.text.includes('Unlimited');
                              const IconComponent = isZeroLimit ? FaTimes : FaCheck;
                              const iconColor = isZeroLimit 
                                ? (isSelected ? 'text-red-500' : 'text-red-500')
                                : (isSelected ? 'text-green-500' : 'text-green-500');
                              
                              return (
                                <li key={index} className="flex items-center">
                                  <IconComponent className={`h-2 w-2 sm:h-2.5 sm:w-2.5 md:h-3 md:w-3 mr-1 sm:mr-1.5 md:mr-2 flex-shrink-0 ${iconColor}`} />
                                  <span className={`text-xs ${isSelected ? 'text-white' : 'text-gray-300'} ${
                                    feature.highlighted 
                                      ? 'text-red-400 font-semibold animate-pulse' 
                                      : ''
                                  }`}>
                                    <span className="sm:hidden">
                                      {feature.text.replace('Unlimited', '∞')}
                                    </span>
                                    <span className="hidden sm:inline">
                                      {feature.text}
                                    </span>
                                  </span>
                                </li>
                              );
                            });
                          })()}
                        </ul>
                      </div>
                    );
                    })}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={onClose}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  
                  {selectedPlan !== (user?.subscription?.plan || 'free') && (
                    <button
                      onClick={handleUpgrade}
                      disabled={isProcessing}
                      className="px-4 sm:px-6 md:px-8 py-2 sm:py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                          <span className="hidden sm:inline">Processing...</span>
                          <span className="sm:hidden">Processing</span>
                        </>
                      ) : (
                        <>
                          <FaCrown className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">Upgrade to {selectedPlan === 'premium' ? 'Premium' : 'Enterprise'}</span>
                          <span className="sm:hidden">Upgrade</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UpgradeModal;
